import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { SearchableSelect } from '@/components/ui/SearchableSelect';
import type { HrOrgStructure } from '@/types/hr';
import { useAppSelector } from '@/hooks/useAppSelector';
import { patchOrgStructure, refreshSessionInit } from '@/store/sessionSlice';
import { hrApi } from '@/api';
import { useSelectLabels } from '@/hooks/useSelectLabels';

type OrgSelectProps = {
  label?: string;
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  className?: string;
  required?: boolean;
  disabled?: boolean;
};

/** Invalidate HR org cache and reload session init (after master-data CRUD). */
export function invalidateOrgStructureCache() {
  void import('@/store').then(({ store }) => {
    store.dispatch(refreshSessionInit(store.getState().app.selectedCompany?.id));
  });
}

export function refreshOrgStructureCache(data: HrOrgStructure) {
  void import('@/store').then(({ store }) => {
    store.dispatch(patchOrgStructure(data));
  });
}

export function fetchOrgStructure() {
  return import('@/store').then(({ store }) => {
    const cached = store.getState().session.orgStructure;
    if (cached) return cached;
    return hrApi.getOrgStructure().then((data) => {
      store.dispatch(patchOrgStructure(data));
      return data;
    });
  });
}

function useHrOrgStructure(enabled: boolean) {
  const structure = useAppSelector((s) => s.session.orgStructure);
  const loading = useAppSelector((s) => s.session.loading && !s.session.loaded);

  return { structure: enabled ? structure : null, loading: enabled ? loading : false };
}

function emptyHint(tab: string, label: string) {
  return (
    <p className="mt-1 text-xs text-slate-500">
      No {label} yet.{' '}
      <Link to={`/hr/master-data?tab=${tab}`} className="text-primary-600 hover:underline">
        Set up in HR master data
      </Link>
    </p>
  );
}

export function DepartmentSelect({
  label = 'Department',
  value = '',
  onChange,
  placeholder,
  className,
  required,
  disabled,
  enabled = true,
}: OrgSelectProps & { enabled?: boolean }) {
  const selectLabels = useSelectLabels();
  const { structure, loading } = useHrOrgStructure(enabled);
  const options = useMemo(
    () =>
      (structure?.departments ?? [])
        .filter((d) => d.active)
        .map((d) => ({ value: String(d.id), label: `${d.name} (${d.code})` })),
    [structure?.departments],
  );

  return (
    <>
      <SearchableSelect
        className={className}
        label={label}
        required={required}
        value={value}
        onChange={onChange}
        options={options}
        placeholder={placeholder ?? selectLabels.selectDepartment}
        disabled={disabled}
        loading={loading}
        searchPlaceholder={selectLabels.searchDepartments}
        emptyMessage={selectLabels.noDepartments}
      />
      {!loading && options.length === 0 && emptyHint('departments', 'departments')}
    </>
  );
}

export function DesignationSelect({
  label = 'Designation',
  value = '',
  onChange,
  placeholder,
  className,
  required,
  disabled,
  enabled = true,
}: OrgSelectProps & { enabled?: boolean }) {
  const selectLabels = useSelectLabels();
  const { structure, loading } = useHrOrgStructure(enabled);
  const options = useMemo(
    () =>
      (structure?.designations ?? [])
        .filter((d) => d.active)
        .map((d) => {
          const gradeLabel = structure?.grades?.find((g) => g.id === d.gradeId)?.code ?? d.grade;
          return {
            value: String(d.id),
            label: gradeLabel ? `${d.name} (${d.code}) · ${gradeLabel}` : `${d.name} (${d.code})`,
          };
        }),
    [structure?.designations, structure?.grades],
  );

  return (
    <>
      <SearchableSelect
        className={className}
        label={label}
        required={required}
        value={value}
        onChange={onChange}
        options={options}
        placeholder={placeholder ?? selectLabels.selectDesignation}
        disabled={disabled}
        loading={loading}
        searchPlaceholder={selectLabels.searchDesignations}
        emptyMessage={selectLabels.noDesignations}
      />
      {!loading && options.length === 0 && emptyHint('designations', 'designations')}
    </>
  );
}

export function GradeSelect({
  label = 'Grade',
  value = '',
  onChange,
  placeholder,
  className,
  required,
  disabled,
  enabled = true,
}: OrgSelectProps & { enabled?: boolean }) {
  const selectLabels = useSelectLabels();
  const { structure, loading } = useHrOrgStructure(enabled);
  const options = useMemo(
    () =>
      (structure?.grades ?? [])
        .filter((g) => g.active)
        .sort((a, b) => a.levelOrder - b.levelOrder)
        .map((g) => ({ value: String(g.id), label: `${g.name} (${g.code}) · L${g.levelOrder}` })),
    [structure?.grades],
  );

  return (
    <>
      <SearchableSelect
        className={className}
        label={label}
        required={required}
        value={value}
        onChange={onChange}
        options={options}
        placeholder={placeholder ?? selectLabels.selectGrade}
        disabled={disabled}
        loading={loading}
        searchPlaceholder={selectLabels.searchGrades}
        emptyMessage={selectLabels.noGrades}
      />
      {!loading && options.length === 0 && emptyHint('grades', 'grades')}
    </>
  );
}

export function BusinessUnitSelect({
  label = 'Business unit',
  value = '',
  onChange,
  placeholder,
  className,
  required,
  disabled,
  enabled = true,
}: OrgSelectProps & { enabled?: boolean }) {
  const selectLabels = useSelectLabels();
  const { structure, loading } = useHrOrgStructure(enabled);
  const options = useMemo(
    () =>
      (structure?.businessUnits ?? [])
        .filter((b) => b.active)
        .map((b) => ({ value: String(b.id), label: `${b.name} (${b.code})` })),
    [structure?.businessUnits],
  );

  return (
    <>
      <SearchableSelect
        className={className}
        label={label}
        required={required}
        value={value}
        onChange={onChange}
        options={options}
        placeholder={placeholder ?? selectLabels.selectBusinessUnit}
        disabled={disabled}
        loading={loading}
        searchPlaceholder={selectLabels.searchBusinessUnits}
        emptyMessage={selectLabels.noBusinessUnits}
      />
      {!loading && options.length === 0 && emptyHint('businessUnits', 'business units')}
    </>
  );
}
